import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Mail, MousePointerClick, Eye, ArrowRight, AlertTriangle, TrendingUp, Loader2,
} from 'lucide-react';

/**
 * "My Email Performance" — sales-rep-scoped widget for the Dashboard.
 *
 * Uses the same `/api/email-tracking/stats?period=week` and `/emails?limit=N`
 * endpoints the Email Analytics page uses. Those endpoints already scope
 * to `sent_by = current_user.id` for non-admin roles, so this widget shows
 * only the rep's own outbound traffic without any client-side filtering.
 *
 * Kept lean intentionally: a rep opens the dashboard first thing in the
 * morning, glances at their delivery + engagement + nudge, then keeps
 * working. Anything more detailed lives in /email-analytics.
 */
export function MyEmailPerformanceWidget() {
  const { API, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [topEmails, setTopEmails] = useState([]);
  const [daysSinceLastSend, setDaysSinceLastSend] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [statsRes, emailsRes] = await Promise.all([
        axios.get(`${API}/email-tracking/stats?period=week`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/email-tracking/emails?limit=50`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(statsRes.data);
      const emails = emailsRes.data?.emails || [];
      // Top 3 by open count (deliberately not by opens+clicks to keep the
      // "which subject line worked" story clear).
      const top = [...emails]
        .filter((e) => (e.open_count || 0) > 0)
        .sort((a, b) => (b.open_count || 0) - (a.open_count || 0))
        .slice(0, 3);
      setTopEmails(top);
      // Nudge trigger: days since last send. Uses ALL fetched emails, not
      // just this week's, so a rep who sent nothing this week but 2 days
      // ago still gets an accurate count.
      if (emails.length > 0) {
        const mostRecent = emails.reduce((acc, e) => {
          const t = new Date(e.sent_at || 0).getTime();
          return t > acc ? t : acc;
        }, 0);
        if (mostRecent > 0) {
          setDaysSinceLastSend(Math.floor((Date.now() - mostRecent) / (1000 * 60 * 60 * 24)));
        }
      }
    } catch {
      // Silently degrade — a broken widget shouldn't break the dashboard.
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Nudge only when there's genuinely no recent activity — not when
  // the rep sent plenty this week but the *most recent* one happens to be
  // 4 days old. Testing agent (iteration_176) flagged the contradictory
  // "No emails sent in N days" appearing next to a non-zero Sent tile.
  const totalSentThisWeek = stats?.total_sent || 0;
  const showNudgeNoActivity =
    !loading && totalSentThisWeek === 0 && (daysSinceLastSend == null || daysSinceLastSend >= 3);
  const clickToOpen = stats?.click_to_open_rate ?? 0;

  return (
    <Card data-testid="my-email-performance-widget">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <Mail className="h-4 w-4 text-blue-600" />
          My Email Performance
          <span className="ml-auto text-[11px] font-normal text-slate-400 uppercase tracking-wide">this week</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-slate-400" data-testid="my-email-loading">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <>
            {/* Headline stats — kept to 3 numbers so a rep can read at a glance */}
            <div className="grid grid-cols-3 gap-3" data-testid="my-email-stats">
              <div className="bg-slate-50 rounded-md p-3 text-center">
                <div className="text-2xl font-bold text-slate-900" data-testid="my-email-sent">{stats?.total_sent || 0}</div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wide mt-0.5">Sent</div>
              </div>
              <div className="bg-blue-50 rounded-md p-3 text-center">
                <div className="text-2xl font-bold text-blue-700" data-testid="my-email-open-rate">{stats?.open_rate || 0}<span className="text-sm ml-0.5">%</span></div>
                <div className="text-[11px] text-blue-600 uppercase tracking-wide mt-0.5 flex items-center justify-center gap-1"><Eye className="h-3 w-3" /> Open rate</div>
              </div>
              <div className="bg-emerald-50 rounded-md p-3 text-center">
                <div className="text-2xl font-bold text-emerald-700" data-testid="my-email-click-to-open">{clickToOpen}<span className="text-sm ml-0.5">%</span></div>
                <div className="text-[11px] text-emerald-600 uppercase tracking-wide mt-0.5 flex items-center justify-center gap-1"><MousePointerClick className="h-3 w-3" /> CTO</div>
              </div>
            </div>

            {/* Zero-activity nudge — only shown when the rep hasn't sent
                anything in ≥3 days. Uses total_sent this week + days since
                last send so a rep who just came back from vacation isn't
                nagged for a 30-day gap either — the visible copy still
                encourages restart. */}
            {showNudgeNoActivity && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm" data-testid="my-email-nudge-no-activity">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-amber-800">
                  <div className="font-medium">
                    {daysSinceLastSend == null
                      ? 'No emails sent yet.'
                      : `No emails sent in ${daysSinceLastSend} day${daysSinceLastSend === 1 ? '' : 's'}.`}
                  </div>
                  <div className="text-xs text-amber-700 mt-0.5">Reach out to a fresh lead today — inbox activity keeps prospects warm.</div>
                </div>
              </div>
            )}

            {/* Top-3 opened emails — the "which subject line worked" list. */}
            {topEmails.length > 0 && (
              <div className="space-y-1.5" data-testid="my-email-top-list">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" /> Top opened this week
                </div>
                {topEmails.map((e, i) => (
                  <div key={e.id || i} className="flex items-center gap-2 py-1.5 px-2 hover:bg-slate-50 rounded text-xs" data-testid={`my-email-top-${i}`}>
                    <span className="w-4 text-slate-400 font-mono">{i + 1}.</span>
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-slate-900 font-medium">{e.subject || '(no subject)'}</div>
                      <div className="truncate text-[11px] text-slate-500">{e.recipient_email}</div>
                    </div>
                    <span className="text-[11px] font-semibold text-blue-700 flex items-center gap-0.5" title={`${e.open_count} opens`}>
                      <Eye className="h-3 w-3" /> {e.open_count}
                    </span>
                    {(e.click_count || 0) > 0 && (
                      <span className="text-[11px] font-semibold text-emerald-700 flex items-center gap-0.5" title={`${e.click_count} clicks`}>
                        <MousePointerClick className="h-3 w-3" /> {e.click_count}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="pt-1">
              <Link to="/email-analytics" data-testid="my-email-see-all-link">
                <Button variant="ghost" size="sm" className="w-full h-8 text-xs text-slate-600 hover:text-slate-900">
                  See full analytics <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </Link>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
